/**
 * Handles the add-to-cart operation.
 */
if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector("form");
        if (!this.form) return;

        this.isLoading = false;
        this.error = false;
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.boundOnSubmit = this.onSubmitHandler.bind(this);
        this.form.addEventListener("submit", this.boundOnSubmit);

        // Bundle Sale form initialization
        this.bundleSale = document.getElementById(
          `Bundle-Sale-${this.dataset.section}`,
        );
        // Care Service
        this.CareService = document.getElementById(
          `Care-Service-${this.dataset.section}`,
        );
        // Listen for changes in the main product quantity and synchronize the quantity with bundled products
        if (this.bundleSale) {
          const QuantityInput = document.getElementById(
            `Quantity-${this.dataset.section}`,
          );
          if (QuantityInput)
            QuantityInput.addEventListener("change", (event) => {
              this.bundleSale.setItemsQuantity(event.target.value);
            });
        }

        this.addCartButton = this.querySelector('button[type="submit"]');
        this.miniAddCartButton = document.getElementById(
          `Mini-Submit-Button-${this.dataset.section}`,
        );

        // Set aria attributes for add-to-cart buttons
        this.cartDrawer = document.getElementById("Cart-Drawer");
        if (
          this.cartDrawer &&
          !this.cartDrawer.hasAttribute("data-status-silence")
        ) {
          if (this.addCartButton) {
            this.addCartButton.setAttribute("aria-haspopup", "dialog");
            this.addCartButton.setAttribute("aria-expanded", "false");
            this.addCartButton.setAttribute("aria-controls", "Cart-Drawer");
          }

          if (this.miniAddCartButton) {
            this.miniAddCartButton.setAttribute("aria-haspopup", "dialog");
            this.miniAddCartButton.setAttribute("aria-expanded", "false");
            this.miniAddCartButton.setAttribute("aria-controls", "Cart-Drawer");
          }
        }

        // Error message container
        this.errorMessageWrapper = document.getElementById(
          `Product-Form-Error-Message-${this.dataset.section}`,
        );


        // Mini checkout
        this.miniChecker = document.getElementById(
          `Mini-Check-${this.dataset.section}`,
        );
        if (this.miniChecker) {
          let isMiniCheckShouldHide = true;
          // Show/hide mini checkout based on product form visibility
          this.productFormObserver = new IntersectionObserver(
            (entries, observer) => {
              const rootBoundsTop = entries[0].rootBounds?.top || 0; // rootBounds may be null inside theme editor iframe
              if (entries[0].isIntersecting) {
                isMiniCheckShouldHide = true;
                this.hideMiniCheck();
              } else if (entries[0].boundingClientRect.top < rootBoundsTop) {
                isMiniCheckShouldHide = false;
                this.showMiniCheck();
              }
            },
            {
              root: null,
              rootMargin: "-200px 0px 200px 0px",
            },
          );
          this.productFormObserver.observe(this);

          // Hide mini checkout when footer is visible to avoid overlap
          const footer = document.getElementById("Page-Footer");
          if (footer) {
            this.footerObserver = new IntersectionObserver(
              (entries) => {
                if (entries[0].isIntersecting) {
                  this.hideMiniCheck();
                } else {
                  if (!isMiniCheckShouldHide) this.showMiniCheck();
                }
              },
              {
                root: null,
                rootMargin: "0px 0px 0px 0px",
              },
            );

            // Observe footer element
            this.footerObserver.observe(footer);
          }

          // Sync main quantity input to mini checkout
          const quantityInput = document.getElementById(
            `Quantity-${this.dataset.section}`,
          );
          if (quantityInput)
            quantityInput.addEventListener("change", (event) => {
              const miniQuantityInput = document.getElementById(
                `Mini-Quantity-${this.dataset.section}`,
              );
              if (miniQuantityInput) {
                miniQuantityInput.value = event.target.value;

                // Dispatch change without bubbling to avoid infinite loop
                miniQuantityInput.dispatchEvent(
                  new Event("change", { bubbles: false }),
                );
              }
            });
        }
      }

      disconnectedCallback() {
        if (this.boundOnSubmit)
          this.form.removeEventListener("submit", this.boundOnSubmit);
        if (this.productFormObserver) this.productFormObserver.disconnect();
        if (this.footerObserver) this.footerObserver.disconnect();
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }

      /**
       * Whether to suppress error messages (e.g. for gift cards)
       * @returns {boolean}
       */
      get hideErrors() {
        return this.dataset.hideErrors === "true";
      }

      /**
       * Handle add-to-cart form submission
       * @param event
       */
      onSubmitHandler(event) {
        if (this.isLoading) return;
        event.preventDefault();

        if (this.addCartButton.hasAttribute("disabled")) return;

        this.handleErrorMessage();
        this.startLoading();

        const config = webvista.fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers[
          "Content-Type"
        ]; /* Browser auto-generates multipart/form-data Content-Type with boundary for FormData */

        const formData = new FormData(this.form);

        // Append bundle sale items
        if (this.bundleSale) {
          const bundleItems = this.bundleSale.getSelectedItems(
            formData.get("id"),
          );

          if (bundleItems.length > 0)
            bundleItems.forEach((item, index) => {
              formData.append(`items[][id]`, item["id"]);
              formData.append(`items[][quantity]`, item["quantity"]);
              formData.append(`items[][parent_id]`, item["parent_id"]);
            });
        }

        // Append care service item
        if (this.CareService) {
          const careId = this.CareService.getSelectedId();
          if (careId) {
            formData.append(`items[][id]`, careId);
            formData.append(`items[][quantity]`, formData.get("quantity"));
            formData.append(`items[][parent_id]`, formData.get("id"));
          }
        }

        formData.append(
          "sections",
          this.getSectionsToRender()
            .map((section) => section.section)
            .join(),
        );
        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        fetch(`${window["routes"]["cart_add_url"]}`, config)
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              webvista.publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);
              return (this.error = true);
            }
            this.error = false;

            // Dynamically update rendered sections
            SectionDynamicUpdate.updateSections(
              this.getSectionsToRender(),
              response["sections"],
            );

            // Publish cart update event
            webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: formData.get("id"),
            });

            if (
              this.cartDrawer &&
              !this.cartDrawer.hasAttribute("data-status-silence")
            ) {
              const isDrawerOpen = this.cartDrawer.hasAttribute("open");

              if (!isDrawerOpen) {
                this.cartDrawer.show(event.submitter);
              }
            } else {
              return (window.location = window["routes"]["cart_url"]);
            }
          })
          .catch((error) => {
            this.handleErrorMessage(
              window["accessibilityStrings"]["unknownError"],
            );
            this.error = true;
          })
          .finally(() => {
            this.endLoading();
          });
      }

      /**
       * Get sections to re-render after cart update
       * @returns {[{section: string, selector: string, id: string}]}
       */
      getSectionsToRender() {
        const sections = [
          {
            id: "Cart-Icon-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
          {
            id: "Bottom-Cart-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
          {
            id: "Cart-Drawer",
            section: "cart-drawer",
            selector: ".cart-count",
          },
        ];

        if (
          this.cartDrawer &&
          !this.cartDrawer.hasAttribute("data-status-silence")
        ) {
          sections.push({
            id: "Cart-Drawer",
            section: this.cartDrawer.dataset.section,
            selector: "#Cart-Drawer-Details",
          });
        }

        return sections;
      }

      /**
       * Show or clear error message
       * @param errorMessage
       */
      handleErrorMessage(errorMessage = null) {
        if (this.hideErrors || !this.errorMessageWrapper) return;

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        this.errorMessageWrapper.textContent = errorMessage ? errorMessage : "";
      }

      startLoading() {
        this.isLoading = true;

        this.addCartButton.setAttribute("disabled", "disabled");
        this.addCartButton.classList.add("loading");

        if (this.miniAddCartButton) {
          this.miniAddCartButton.setAttribute("disabled", "disabled");
          this.miniAddCartButton.classList.add("loading");
        }
      }

      endLoading() {
        this.isLoading = false;

        this.addCartButton.removeAttribute("disabled");
        this.addCartButton.classList.remove("loading");

        if (this.miniAddCartButton) {
          this.miniAddCartButton.removeAttribute("disabled");
          this.miniAddCartButton.classList.remove("loading");
        }

        // Close quick view drawer on success
        if (!this.error) {
          this.closest("product-quick-view-drawer")?.hide();
        }
      }

      showMiniCheck() {
        if (!this.miniChecker) return;
        this.miniChecker.classList.remove("hiding");
        this.miniChecker.classList.add("active");
      }

      hideMiniCheck() {
        if (!this.miniChecker || !this.miniChecker.classList.contains("active"))
          return;
        this.miniChecker.classList.add("hiding");
        this.miniChecker.addEventListener(
          "animationend",
          () => {
            this.miniChecker.classList.remove("active", "hiding");
          },
          { once: true },
        );
      }
    },
  );
}
